import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';

import { LanguagePill } from '@/components/chrome/LanguagePill';

/**
 * Chrome for the customer-facing magic link pages.
 *
 * This is the one surface in the product people reach by clicking a link in an
 * email while holding no account, so it carries the branding the rest of the
 * app can take for granted: an unbranded page behind an emailed token is what
 * phishing looks like.
 *
 * Deliberately a bar rather than a nav. A magic link session is
 * single-purpose — there is nowhere else for the customer to go — so links
 * would either sit empty or walk them out of the portal mid-task.
 *
 * The footer reaches /privacy, /terms and /cookies, which already exist as
 * routes and which nothing on these pages previously linked to. The cookie
 * banner runs here, and for an EU product a standing route to the cookie
 * policy is expected.
 */
export function CustomerPageShell({
  actions,
  children,
}: {
  /** Rendered in the bar beside the language pill — the sign-out control. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation('myBookings');

  return (
    <div className="flex min-h-screen flex-col bg-surface-2">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link href="/" className="inline-flex items-center">
            <img
              src="/images/logo_horizontal_transparent.png"
              alt="Sloty"
              className="h-7 w-auto"
            />
          </Link>
          <div className="flex items-center gap-2">
            <LanguagePill />
            {actions}
          </div>
        </div>
      </header>

      <main className="flex-grow">{children}</main>

      <footer className="mt-auto border-t border-line bg-surface">
        <div className="mx-auto flex max-w-3xl flex-col gap-3 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p className="text-body-xs text-text-muted">{t('footer.poweredBy')}</p>
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link href="/privacy" className="text-body-xs text-soft hover:text-ink">
              {t('footer.privacy')}
            </Link>
            <Link href="/terms" className="text-body-xs text-soft hover:text-ink">
              {t('footer.terms')}
            </Link>
            <Link href="/cookies" className="text-body-xs text-soft hover:text-ink">
              {t('footer.cookies')}
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
